import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';

import { useAuthStore } from '../store/auth';

/**
 * Wraps a protected route. Decides one of three outcomes on render:
 *
 *   1. If we have an access token in memory, render the children.
 *   2. If we have a refresh token but no access token (typical cold-start
 *      after the user reopened the PWA), kick off /auth/refresh and show
 *      a loading state until it resolves.
 *   3. Otherwise (no tokens at all), redirect to /login.
 *
 * The api/client.js interceptor handles 401-during-normal-use refresh on
 * its own; this component only handles the cold-start case.
 */
export default function AuthGuard({ children }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const location = useLocation();

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!accessToken && refreshToken) {
      setRefreshing(true);
      axios
        .post(
          `${import.meta.env.VITE_API_BASE || '/api/v1'}/auth/refresh`,
          { refresh_token: refreshToken },
        )
        .then((response) => {
          useAuthStore.setState({
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
          });
        })
        .catch(() => {
          // Refresh failed — the persisted refresh token is stale or revoked.
          // Wipe local state; the redirect-to-login below will fire on next
          // render.
          clear();
        })
        .finally(() => setRefreshing(false));
    }
  }, [accessToken, refreshToken, clear]);

  if (refreshing) {
    return (
      <main className="min-h-full flex items-center justify-center px-4">
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (!accessToken && !refreshToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
